<template>
  <admin-modal size="lg" :title="`Approve ${doctor.doctorname}`"
               subtitle="Assign the doctor to a clinic. Approving emails their login details."
               :busy="saving" @close="$emit('close')">
    <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

    <div class="adm-approve-doctor">
      <span class="adm-avatar adm-avatar--sm">{{ initials(doctor.doctorname) }}</span>
      <div>
        <span class="adm-person__name">{{ doctor.doctorname }}</span>
        <span class="adm-person__meta">
          {{ doctor.specialization || "Specialization not set" }} · {{ doctor.email || "No email" }}
        </span>
      </div>
    </div>

    <div v-if="linked.length" class="adm-alert adm-alert--info">
      <i class="fas fa-info-circle"></i>
      <div>
        Already assigned to
        <strong v-for="(clinic, i) in linked" :key="clinic.clinicid">{{ clinic.clinicname }}{{ i < linked.length - 1 ? ", " : "" }}</strong>.
        You can approve now or add another clinic.
      </div>
    </div>

    <div class="adm-tabs adm-approve-tabs" role="tablist">
      <button type="button" class="adm-tab" :class="{ 'is-active': mode === 'existing' }" role="tab"
              :aria-selected="mode === 'existing'" @click="mode = 'existing'">
        <i class="fas fa-hospital"></i> Assign existing clinic
      </button>
      <button type="button" class="adm-tab" :class="{ 'is-active': mode === 'new' }" role="tab"
              :aria-selected="mode === 'new'" @click="mode = 'new'">
        <i class="fas fa-plus"></i> Create new clinic
      </button>
    </div>

    <div v-if="mode === 'existing'">
      <div class="adm-search adm-approve-search">
        <i class="fas fa-search"></i>
        <input v-model.trim="search" class="adm-input" placeholder="Search clinics by name, ID or city" />
      </div>
      <div v-if="loading" class="adm-options">
        <div v-for="n in 3" :key="n" class="adm-option"><div class="adm-skeleton" style="width: 60%"></div></div>
      </div>
      <div v-else-if="!clinics.length" class="adm-empty">
        <i class="fas fa-hospital"></i>
        <strong>No clinics yet</strong>
        Create the doctor's clinic in the “Create new clinic” tab.
      </div>
      <div v-else-if="!filteredClinics.length" class="adm-empty">No clinic matches “{{ search }}”.</div>
      <div v-else class="adm-options">
        <label v-for="clinic in filteredClinics" :key="clinic.clinicid" class="adm-option"
               :class="{ 'is-checked': isChecked(clinic), 'is-disabled': isLinked(clinic) }">
          <input type="checkbox" :checked="isChecked(clinic)" :disabled="isLinked(clinic)"
                 @change="toggle(clinic.clinicid)" />
          <span class="adm-option__text">
            <span class="adm-option__title">{{ clinic.clinicname }}</span>
            <span class="adm-option__meta">
              <span class="adm-mono">{{ clinic.clinicid }}</span> · {{ clinic.locationname || "No city" }}
              · {{ clinic.doctor_count }} doctor{{ clinic.doctor_count === 1 ? "" : "s" }}
            </span>
          </span>
          <span v-if="isLinked(clinic)" class="adm-badge adm-badge--info adm-badge--plain">Assigned</span>
        </label>
      </div>
    </div>

    <div v-else>
      <clinic-form-fields v-model="newClinic" :cities="cities" :errors="clinicErrors" />
    </div>

    <template #footer>
      <span class="adm-muted adm-approve-summary">{{ summary }}</span>
      <button type="button" class="adm-btn" :disabled="saving" @click="$emit('close')">Cancel</button>
      <button type="button" class="adm-btn adm-btn--success" :disabled="saving || loading || !canApprove" @click="approve">
        <span v-if="saving" class="adm-spinner"></span>
        <i v-else class="fas fa-check"></i>
        Approve &amp; send login
      </button>
    </template>
  </admin-modal>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import ClinicFormFields, { clinicPayload, emptyClinic, validateClinic } from "./ClinicFormFields.vue";
import { errorMessage, initials } from "../admin-utils";

export default {
  name: "ApproveDoctorModal",
  components: { AdminModal, ClinicFormFields },
  props: {
    doctor: { type: Object, required: true },
    cities: { type: Array, default: () => [] },
  },
  emits: ["close", "approved"],
  data() {
    return {
      mode: "existing",
      loading: true,
      saving: false,
      error: "",
      clinics: [],
      linked: [],
      selected: [],
      search: "",
      newClinic: emptyClinic(),
      clinicErrors: {},
    };
  },
  computed: {
    linkedIds() {
      return new Set(this.linked.map(c => c.clinicid));
    },
    filteredClinics() {
      const term = this.search.toLowerCase();
      if (!term) return this.clinics;
      return this.clinics.filter(c =>
        [c.clinicname, c.clinicid, c.locationname].some(v => (v || "").toLowerCase().includes(term)));
    },
    canApprove() {
      if (this.mode === "new") return true; // validated on submit
      return this.selected.length > 0 || this.linked.length > 0;
    },
    summary() {
      if (this.mode === "new") return this.newClinic.clinicname ? `Creates “${this.newClinic.clinicname}”` : "";
      const count = this.selected.length;
      return count ? `${count} clinic${count === 1 ? "" : "s"} selected` : "";
    },
  },
  methods: {
    initials,
    isLinked(clinic) {
      return this.linkedIds.has(clinic.clinicid);
    },
    toggle(clinicid) {
      const i = this.selected.indexOf(clinicid);
      if (i === -1) this.selected.push(clinicid);
      else this.selected.splice(i, 1);
    },
    isChecked(clinic) {
      return this.isLinked(clinic) || this.selected.includes(clinic.clinicid);
    },
    async load() {
      this.loading = true;
      try {
        const [clinics, linked] = await Promise.all([
          axios.get("/admin/clinics"),
          axios.get(`/admin/doctors/${this.doctor.doctorid}/clinics`),
        ]);
        this.clinics = clinics.data || [];
        this.linked = linked.data || [];
        if (!this.clinics.length) this.mode = "new";
      } catch (error) {
        this.error = errorMessage(error, "Could not load clinics.");
      } finally {
        this.loading = false;
      }
    },
    async approve() {
      this.error = "";
      const body = {};
      if (this.mode === "new") {
        this.clinicErrors = validateClinic(this.newClinic);
        if (Object.keys(this.clinicErrors).length) return;
        body.new_clinic = clinicPayload(this.newClinic);
      } else {
        body.clinicids = this.selected;
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/admin/doctors/${this.doctor.doctorid}/approve`, body);
        this.$emit("approved", data);
      } catch (error) {
        this.error = errorMessage(error, "Could not approve the doctor.");
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.adm-approve-doctor {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  background: #f9fafb;
  margin-bottom: 16px;
}

.adm-approve-tabs {
  margin-bottom: 16px;
}

.adm-approve-search {
  max-width: none;
  margin-bottom: 12px;
}

.adm-approve-summary {
  margin-right: auto;
  align-self: center;
  font-size: 13px;
}
</style>
